import './VisualPicker.scss';

import classNames from 'classnames';
import { SonarText } from '@ui/text';

import { Else, If, Then, When } from 'react-if';
import { Form, Radio, RadioChangeEvent, Tooltip } from 'antd';
import Icon, { CheckOutlined } from '@ant-design/icons';
import React, { ReactNode, useCallback } from 'react';

interface VisualPickerOptions {
    label: string | ReactNode,
    value: string | number,
    disabled?: boolean
}

export interface VisualPickerProps {
	disabled?: boolean;
    hidden?: boolean;
	groupClassname?: string;
	groupItemClassname?: string;
    onChange?: (value: string | number | undefined) => void;
    options: Array<VisualPickerOptions>;
    name: string | number | Array<string | number>;
	message?: string;
	title?: string;
	value?: string | number;
}

export const VisualPicker: React.FC<VisualPickerProps> = ({
	disabled,
	groupClassname,
	groupItemClassname,
	hidden = false,
	message,
	name,
	options,
	onChange,
	title,
	value
}) => {
	const handleOnChange = useCallback((e: RadioChangeEvent) => {
		if (onChange) {
			onChange(e.target.value);
		}
	}, [ onChange ]);

	const handleOnReset = useCallback(() => {
		if (onChange) {
			onChange(undefined);
		}
	}, [ onChange ]);

	const handleClick = useCallback((clickValue: string | number) => {
		if (clickValue === value) {
			handleOnReset();
		}
	}, [ handleOnReset, value ]);

	return (
		<>
			<When condition={ !hidden }>
				<SonarText className='h2'>
					{ title }
				</SonarText>
			</When>
			<Form.Item
				className='visual-picker'
				hidden={ hidden }
				initialValue={ value }
				name={ name }
				onReset={ handleOnReset }
				valuePropName='checked'>
				<Radio.Group
					className={ classNames('visual-picker-group', groupClassname) }
					defaultValue={ value }
					disabled={ disabled }
					onChange={ handleOnChange }
					value={ value }>
					{ options.map((option) =>
						<Tooltip
							key={ option.value }
							open={ option.disabled ? undefined : false }
							placement='bottom'
							title={ message }>
							<Radio.Button
								className={ classNames('visual-picker-group-item', groupItemClassname) }
								disabled={ option.disabled || disabled }
								onClick={ () => handleClick(option.value) }
								value={ option.value }>
								<If condition={ typeof option.label === 'string' }>
									<Then>
										<SonarText
											className='visual-picker-group-item-title'
											weight='semibold'>
											{ option.label }
										</SonarText>
									</Then>
									<Else>
										{ option.label }
									</Else>
								</If>
								<When condition={ !option.disabled || option.value === value }>
									<Icon
										className='visual-picker-checkmark-area'
										component={
											() => <CheckOutlined className='visual-picker-checkmark-icon' />
										} />
								</When>
							</Radio.Button>
						</Tooltip>) }
				</Radio.Group>
			</Form.Item>
		</>);
};
